<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import efiConfigAPI from 'dashboard/api/efiConfig';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();

const isLoading = ref(true);
const isSaving = ref(false);
const status = ref({});
const certificate = ref(null);
const certificateInput = ref(null);

const form = ref({
  client_id: '',
  client_secret: '',
  pix_key: '',
  environment: 'production',
  enabled: false,
});

const secretPlaceholder = isSet =>
  isSet ? t('INTEGRATION_SETTINGS.EFI.FORM.CONFIGURED') : '';

const formatDate = value => (value ? new Date(value).toLocaleString() : '');

const connectedLabel = computed(() =>
  t('INTEGRATION_SETTINGS.EFI.STATUS.CONNECTED', {
    date: formatDate(status.value.webhook_registered_at),
  })
);

const applyData = data => {
  status.value = data;
  form.value = {
    client_id: '',
    client_secret: '',
    pix_key: data.pix_key || '',
    environment: data.environment || 'production',
    enabled: Boolean(data.enabled),
  };
  certificate.value = null;
  if (certificateInput.value) certificateInput.value.value = '';
};

const loadConfig = async () => {
  try {
    const { data } = await efiConfigAPI.get();
    applyData(data);
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.EFI.API.LOAD_ERROR'));
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await loadConfig();
  } finally {
    isLoading.value = false;
  }
});

const onCertificate = event => {
  [certificate.value] = event.target.files;
};

const saveConfig = async () => {
  isSaving.value = true;
  try {
    const { data } = await efiConfigAPI.update({
      ...form.value,
      certificate: certificate.value,
    });
    applyData(data);
    useAlert(t('INTEGRATION_SETTINGS.EFI.API.SAVE_SUCCESS'));
  } catch (error) {
    const data = error?.response?.data;
    if (data?.configured) applyData(data);
    useAlert(data?.error || t('INTEGRATION_SETTINGS.EFI.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('INTEGRATION_SETTINGS.EFI.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.EFI.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.EFI.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <div
          class="bg-n-alpha-2 border border-n-weak rounded-md p-4 text-sm text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.EFI.INTRO') }}
        </div>

        <div
          v-if="status.enabled && status.webhook_registered_at"
          class="rounded-md border border-n-teal-6 bg-n-teal-2 p-3 text-sm text-n-teal-11"
        >
          {{ connectedLabel }}
        </div>
        <div
          v-else-if="status.last_error"
          class="rounded-md border border-n-ruby-7 bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
        >
          {{ status.last_error }}
        </div>

        <form
          class="flex flex-col gap-4 border border-n-weak rounded-md p-6 bg-n-alpha-3"
          @submit.prevent="saveConfig"
        >
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.EFI.FORM.ENVIRONMENT') }}
            </span>
            <select v-model="form.environment" class="form-select">
              <option value="production">
                {{ $t('INTEGRATION_SETTINGS.EFI.FORM.ENV_PRODUCTION') }}
              </option>
              <option value="homologation">
                {{ $t('INTEGRATION_SETTINGS.EFI.FORM.ENV_HOMOLOGATION') }}
              </option>
            </select>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.EFI.FORM.CLIENT_ID') }}
            </span>
            <input
              v-model="form.client_id"
              type="password"
              autocomplete="new-password"
              class="form-input"
              :placeholder="secretPlaceholder(status.client_id_set)"
            />
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.EFI.FORM.CLIENT_SECRET') }}
            </span>
            <input
              v-model="form.client_secret"
              type="password"
              autocomplete="new-password"
              class="form-input"
              :placeholder="secretPlaceholder(status.client_secret_set)"
            />
            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.EFI.FORM.SECRET_HINT') }}
            </span>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.EFI.FORM.CERTIFICATE') }}
            </span>
            <input
              ref="certificateInput"
              type="file"
              accept=".p12"
              class="text-sm text-n-slate-12"
              @change="onCertificate"
            />
            <span class="text-xs text-n-slate-11">
              {{
                status.certificate_set
                  ? $t('INTEGRATION_SETTINGS.EFI.FORM.CERTIFICATE_SET')
                  : $t('INTEGRATION_SETTINGS.EFI.FORM.CERTIFICATE_HINT')
              }}
            </span>
          </label>

          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.EFI.FORM.PIX_KEY') }}
            </span>
            <input v-model="form.pix_key" type="text" class="form-input" />
          </label>

          <label class="flex items-center gap-2">
            <input v-model="form.enabled" type="checkbox" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.EFI.FORM.ENABLED') }}
            </span>
          </label>
          <span class="text-xs text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.EFI.FORM.ENABLED_HINT') }}
          </span>

          <div class="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              icon="i-lucide-save"
              :label="$t('INTEGRATION_SETTINGS.EFI.FORM.SAVE')"
              :is-loading="isSaving"
            />
          </div>
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>
